import { useState } from "react"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import { useForm } from "react-hook-form"
import classNames from "classnames"
import { FormattedMessage, useIntl } from "react-intl"

import { SignupInput } from "../../../common"

import { useLogout } from "../../../../hooks/useLogout"
import { deleteAccount } from "../../../../services/auth"

const DeleteAccountForm = () => {
  const [loading, setLoading] = useState(false)
  const [editable, setEditable] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  
  const userEmail = useSelector((state) => state.auth.user.email)
  const { handleLogout: logoutUser } = useLogout()

  const { formatMessage } = useIntl()

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    getValues,
    watch,
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      confirmationText: "",
    },
  })

  watch(["confirmationText"])

  const values = getValues()
  const isConfirmationValid = values.confirmationText === userEmail

  const onSubmit = async () => {
    if (loading || !isConfirmationValid) return;

    try {
      setLoading(true)

      await deleteAccount();
      toast.success(formatMessage({ id: "toast.accountDeletedSuccessfully" }))
      logoutUser();
      
      // Reset form state
      setEditable(false)
      setShowConfirmation(false)
      reset()
    } catch (error) {
      toast.error(formatMessage({ id: "toast.somethingWentWrongTryAgain" }))
    }
    setLoading(false)
  }

  const handleCancel = () => {
    setEditable(false)
    setShowConfirmation(false)
    reset()
  }

  const handleEnableEditing = () => {
    setEditable(true)
    setShowConfirmation(true)
  }

  return (
    <form className="details-form" onSubmit={handleSubmit(onSubmit)}>
      <div className="details-form__inputs">
        {!editable && (
          <div className="delete-account-warning">
            <p className="delete-account-warning__text">
              <FormattedMessage id="common.deleteAccountWarning" />
            </p>
            <br />
            <button
              type="button"
              className="btn warning btn--small btn--danger"
              onClick={handleEnableEditing}
            >
              <FormattedMessage id="common.deleteAccount" />
            </button>
          </div>
        )}

        {editable && showConfirmation && (
          <>
            <div className="delete-account-confirmation">
              <p className="delete-account-confirmation__warning">
                <FormattedMessage id="common.deleteAccountWarning" />
              </p>
              <br />
              <p className="delete-account-confirmation__instruction">
                <FormattedMessage 
                  id="common.deleteAccountConfirmationInstruction" 
                  values={{ confirmText: <strong>{userEmail}</strong> }}
                />
              </p>
            </div>

            <SignupInput
              {...{
                name: "confirmationText",
                placeholder: formatMessage({ id: "common.typeEmailToConfirmPlaceholder" }),
                register: register("confirmationText", {
                  required: formatMessage({ id: "common.confirmationRequired" }),
                  validate: (value) => 
                    value === userEmail || formatMessage({ id: "common.invalidConfirmationText" }),
                }),
                error: errors.confirmationText?.message,
                touched: touchedFields.confirmationText,
                value: values.confirmationText,
                type: "text",
              }}
            />
          </>
        )}
      </div>

      {editable && showConfirmation && (
        <div className="details-form__buttons">
          <button
            className={classNames("btn warning btn--small btn--danger", {
              disabled: loading || !isConfirmationValid,
            })}
          >
            {loading ? (
              <FormattedMessage id="common.deleting" />
            ) : (
              <FormattedMessage id="common.deleteAccountConfirm" />
            )}
          </button>
          <button
            className="btn btn--small tertiary"
            type="button"
            onClick={handleCancel}
          >
            <FormattedMessage id="common.cancel" />
          </button>
        </div>
      )}
    </form>
  )
}

export default DeleteAccountForm